import React, { useState, useEffect } from 'react';
import { FiX, FiPhone, FiUser, FiMapPin, FiDollarSign } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './addRetailer.css'; // Reuse the same CSS

const EditRetailer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    contact_number: '',
    email: '',
    address: '',
    due_amount: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [balanceEditable, setBalanceEditable] = useState(false);

  useEffect(() => {
    const fetchRetailer = async () => {
      try {
        const response = await axios.get(`/retailers/${id}`);
        const retailer = response.data;
        setFormData({
          name: retailer.name,
          contact_number: retailer.contact_number,
          email: retailer.email || '',
          address: retailer.address,
          due_amount: retailer.due_amount ?? ''
        });
        setBalanceEditable(Boolean(retailer.balance_editable));
      } catch (err) {
        console.error('Error fetching retailer:', err);
        setApiError(err.response?.data?.error || 'Failed to load retailer data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRetailer();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Retailer name is required';
    }
    if (!formData.contact_number.trim()) {
      newErrors.contact_number = 'Contact number is required';
    } else if (!/^[0-9]{10,15}$/.test(formData.contact_number)) {
      newErrors.contact_number = 'Enter a valid contact number';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (balanceEditable && formData.due_amount !== '' && (isNaN(formData.due_amount) || Number(formData.due_amount) < 0)) {
      newErrors.due_amount = 'Enter a valid non-negative amount';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        await axios.put(`/retailers/${id}`, {
          name: formData.name,
          contact_number: formData.contact_number,
          email: formData.email || null,
          address: formData.address,
          ...(balanceEditable ? { due_amount: formData.due_amount || 0 } : {})
        });
        navigate('/retailers');
      } catch (err) {
        console.error('Error updating retailer:', err);
        setApiError(err.response?.data?.error || 'Failed to update retailer. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading) {
    return <div className="loading">Loading retailer data...</div>;
  }

  return (
    <div className="add-retailer-container">
      <div className="add-retailer-header">
        <h1 className="add-retailer-title">Edit Retailer</h1>
        <button 
          className="cancel-btn"
          onClick={() => navigate('/retailers')}
          disabled={isSubmitting}
        >
          <FiX /> Cancel
        </button>
      </div>

      {apiError && (
        <div className="api-error-message">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="retailer-form">
        <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
          <h3>Retailer Information</h3>
          <label htmlFor="name">
            <FiUser className="input-icon" />
            Retailer Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter retailer name"
            className={errors.name ? 'error-input' : ''}
            disabled={isSubmitting}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className={`form-group ${errors.contact_number ? 'has-error' : ''}`}>
          <label htmlFor="contact_number">
            <FiPhone className="input-icon" />
            Contact Number
          </label>
          <input
            type="tel"
            id="contact_number"
            name="contact_number"
            value={formData.contact_number}
            onChange={handleChange}
            placeholder="Enter 10 digit number"
            className={errors.contact_number ? 'error-input' : ''}
            disabled={isSubmitting}
          />
          {errors.contact_number && <span className="error-message">{errors.contact_number}</span>}
        </div>

        <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter retailer email"
            disabled={isSubmitting}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className={`form-group ${errors.address ? 'has-error' : ''}`}>
          <label htmlFor="address">Address *</label>
          <input
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Enter retailer address"
            className={errors.address ? 'error-input' : ''}
            disabled={isSubmitting}
          />
          {errors.address && <span className="error-message">{errors.address}</span>}
        </div>

        {balanceEditable ? (
          <div className={`form-group ${errors.due_amount ? 'has-error' : ''}`}>
            <label htmlFor="due_amount">
              <FiDollarSign className="input-icon" />
              Opening Balance
            </label>
            <input
              type="number"
              id="due_amount"
              name="due_amount"
              min="0"
              step="0.01"
              value={formData.due_amount}
              onChange={handleChange}
              placeholder="Enter opening balance"
              className={errors.due_amount ? 'error-input' : ''}
              disabled={isSubmitting}
              onWheel={(e) => e.target.blur()}
            />
            {errors.due_amount && <span className="error-message">{errors.due_amount}</span>}
            <span className="field-hint">
              Editable now because no sales or payments have been recorded yet.
            </span>
          </div>
        ) : (
          <div className="form-group">
            <label>
              <FiDollarSign className="input-icon" />
              Current Due Amount
            </label>
            <p className="field-readonly-value">
              Rs. {Number(formData.due_amount || 0).toLocaleString('ne-NP', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <span className="field-hint">
              Locked because sales or payments already exist for this retailer — correct the underlying sale/payment record instead.
            </span>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Update Retailer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRetailer;